import React, { useState } from 'react'
import {
    ArrowLeftIcon,
    BoltIcon,
    BuildingOffice2Icon,
} from '@heroicons/react/24/outline'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import Panel from '../../../../../components/Panel'
import Badge from '../../../../../components/Badge'
import Button from '../../../../../components/Form/Button'
import DisplayField from '../../../../../components/Form/DisplayField'
import CancellationPanel from './CancellationPanel'
import { IncentivePlan } from '../../../../../apis/types'
import { cancelKingIncentivePlanApi } from '../../../../../apis/apis'
import { pushToast } from '../../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../../shared/redux/hooks'
import { addMonths } from 'date-fns'
import { formatUTCDetailedDate, getApiErrorMessage } from '../../../../../util/string'
import { useIncentivePlanAuditHistory } from './useIncentivePlanAuditHistory'
import IncentiveActivityLogPanel from './IncentiveActivityLogPanel'
import { getCancelledCompletedDetails, getCreatedDetails } from './incentiveDisplay'

const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0)
}

interface PaymentPlanDetailProps {
    plan: IncentivePlan
    planAccountType: 'King' | 'Utility'
    utilityName?: string
    hasCancelPermission: boolean
    onBack: () => void
    onCancelled: () => void | Promise<void>
    tenantAccountId: string
}

const PaymentPlanDetail: React.FC<PaymentPlanDetailProps> = ({
    plan,
    planAccountType,
    utilityName,
    hasCancelPermission,
    onBack,
    onCancelled,
    tenantAccountId,
}) => {
    const dispatch = useAppDispatch()
    const [showCancelForm, setShowCancelForm] = useState(false)
    const [cancelSubmitting, setCancelSubmitting] = useState(false)
    const { auditHistory, isLoading: auditLoading } = useIncentivePlanAuditHistory(tenantAccountId, plan.id)

    const isUtilityPlan = planAccountType === 'Utility'
    const isKEPlan = planAccountType === 'King'
    const isActive = plan.status === 'ACTIVE'

    const startingBalance = parseFloat(plan.startingBalance as string)

    const BILLED_STATUSES = ['PAID', 'BILLED']
    const paidOrBilledInstallments = plan.installments.filter((i) => BILLED_STATUSES.includes(i.status))
    const amountBilledFromInstallments = paidOrBilledInstallments.reduce(
        (sum, i) => sum + (Number((i as any).paidAmount) || Number(i.amount) || 0),
        0
    )
    const remainingBalance = plan.installments
        .filter((i) => !BILLED_STATUSES.includes(i.status) && i.status !== 'CANCELLED')
        .reduce((sum, i) => sum + Number(i.amount), 0)
    const amountPaid =
        amountBilledFromInstallments > 0 ? amountBilledFromInstallments : startingBalance - remainingBalance

    const paidOrCreditedCount = paidOrBilledInstallments.length
    const progressPercent =
        plan.installmentCount > 0
            ? Math.round((paidOrCreditedCount / plan.installmentCount) * 100)
            : 0

    const handleCancelSubmit = async (reason: string) => {
        setCancelSubmitting(true)
        try {
            await cancelKingIncentivePlanApi(tenantAccountId, plan.id, reason)
            await onCancelled()
            setShowCancelForm(false)
            dispatch(pushToast({ message: 'Payment plan cancelled successfully', type: 'success' }))
        } catch (e: any) {
            dispatch(
                pushToast({
                    message: 'Failed to cancel payment plan',
                    type: 'error',
                    description: getApiErrorMessage(e),
                })
            )
        } finally {
            setCancelSubmitting(false)
        }
    }

    const createdDetails = getCreatedDetails(plan.createdAt, plan.createdBy, plan.memo)
    const cancelledCompletedDetails = getCancelledCompletedDetails(plan)

    const planLabel =
        planAccountType === 'King' && plan.type === 'PAYMENT_PLAN'
            ? 'King Energy Payment Plan'
            : planAccountType === 'Utility' && plan.type === 'PAYMENT_PLAN'
                ? `${utilityName ?? 'Utility'} Payment Plan`
                : `${planAccountType} ${plan.cadence?.replaceAll('_', ' ')} ${plan.type?.replaceAll('_', ' ')}`

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={onBack}
                    className="flex items-center gap-1 rounded-md border border-indigo-600 dark:border-indigo-400 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back to Incentives
                </button>
                <div className="flex items-center gap-2">
                    {(isKEPlan || isUtilityPlan) && isActive && (
                        hasCancelPermission ? (
                            <>
                                {!showCancelForm && (
                                    <Button size={2} color="red" onClick={() => setShowCancelForm(true)}>
                                        Cancel Plan
                                    </Button>
                                )}
                            </>
                        ) : (
                            <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                    <InformationCircleIcon className="w-4 h-4 shrink-0" />
                                    Sufficient permission required to cancel {isKEPlan ? 'a King Energy' : 'an externally managed'} payment plan.
                            </span>
                        )
                    )}
                </div>
            </div>

            {/* Title row */}
            <div className="flex items-center gap-3 flex-wrap">
                {isKEPlan && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-purple-100 dark:bg-purple-900/40 shrink-0">
                        <BoltIcon className="w-4 h-4 text-purple-700 dark:text-purple-400" />
                    </div>
                )}
                {isUtilityPlan && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-orange-100 dark:bg-orange-900/40 shrink-0">
                        <BuildingOffice2Icon className="w-4 h-4 text-orange-700 dark:text-orange-400" />
                    </div>
                )}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{planLabel}</h3>
                {(plan.humanId ?? plan.id) && (
                    <span className="text-sm font-mono text-gray-500 dark:text-gray-400">
                        {plan.humanId ?? plan.id}
                    </span>
                )}
                <Badge status={plan.status} />
            </div>

            {/* Inline cancel form */}
            {showCancelForm && (
                <CancellationPanel
                    title="Cancel payment plan"
                    warningMessage={
                        <>
                            Cancelling this plan will make the remaining balance of{' '}
                            <strong>{formatCurrency(remainingBalance)}</strong> due immediately.
                        </>
                    }
                    reasonLabel="Cancellation reason"
                    reasonPlaceholder="Describe the reason for cancellation..."
                    confirmActionLabel="Confirm cancellation"
                    dismissActionLabel="Keep plan"
                    confirmModalTitle="Cancel this payment plan?"
                    summaryRows={[
                        {
                            label: 'Plan',
                            value: plan.humanId ?? plan.id,
                            valueClassName: 'font-mono font-medium dark:text-gray-200',
                        },
                        {
                            label: 'Remaining balance due',
                            value: formatCurrency(remainingBalance),
                            valueClassName: 'font-medium text-red-600 dark:text-red-400',
                        },
                    ]}
                    isSubmitting={cancelSubmitting}
                    onConfirm={handleCancelSubmit}
                    onDismiss={() => setShowCancelForm(false)}
                />
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Plan Details */}
                <Panel header="Plan Details">
                    <div className="space-y-3 pt-1">
                        <DisplayField label="Duration">{plan.installmentCount} installments</DisplayField>
                        <DisplayField label="Start Date">
                            {formatUTCDetailedDate(new Date(plan.startDate))}
                        </DisplayField>
                        <DisplayField label="Est. Last Payment">
                            {plan.installments.length > 0
                                ? formatUTCDetailedDate(new Date(plan.installments[plan.installments.length - 1].dueDate))
                                : formatUTCDetailedDate(addMonths(new Date(plan.startDate), plan.installmentCount - 1))}
                        </DisplayField>
                        {plan.endDate && (
                            <DisplayField label="End Date">
                                {formatUTCDetailedDate(new Date(plan.endDate))}
                            </DisplayField>
                        )}
                        <DisplayField label="Created">
                            <>
                                {createdDetails.dateByText && <span className="block">{createdDetails.dateByText}</span>}
                                {createdDetails.reason && <span className="block text-xs text-gray-500 dark:text-gray-400">{createdDetails.reason}</span>}
                            </>
                        </DisplayField>
                        {cancelledCompletedDetails &&
                            (cancelledCompletedDetails.dateByText || cancelledCompletedDetails.reason) && (
                            <DisplayField label="Cancelled/Completed">
                                <>
                                    {cancelledCompletedDetails.dateByText && (
                                        <span className="block">{cancelledCompletedDetails.dateByText}</span>
                                    )}
                                    {cancelledCompletedDetails.reason && (
                                        <span className="block text-xs text-gray-500 dark:text-gray-400">{cancelledCompletedDetails.reason}</span>
                                    )}
                                </>
                            </DisplayField>
                        )}
                    </div>
                </Panel>

                {/* Financial Summary */}
                <Panel header="Financial Summary">
                    <div className="space-y-4 pt-1">
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Total Amount</p>
                            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                                {formatCurrency(startingBalance)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Amount Billed</p>
                            <p className="text-lg font-medium text-green-600 dark:text-green-400">
                                {formatCurrency(amountPaid)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Remaining Balance</p>
                            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                {formatCurrency(remainingBalance)}
                            </p>
                        </div>
                    </div>
                </Panel>

                {/* Progress */}
                <Panel header="Progress">
                    <div className="space-y-4 pt-1">
                        <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Installments Paid</p>
                            <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                                {paidOrCreditedCount} of {plan.installmentCount}
                            </p>
                        </div>
                        <div className="pt-1">
                            <div className="h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-green-500 dark:bg-green-400 transition-all"
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{progressPercent}% complete</p>
                        </div>
                        {plan.status === 'CANCELLED' && (plan as any).cancellationReason && (
                            <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Cancellation Reason</p>
                                <p className="text-sm font-medium text-red-600 dark:text-red-400">
                                    {(plan as any).cancellationReason}
                                </p>
                            </div>
                        )}
                    </div>
                </Panel>
            </div>

            {/* Installment Schedule */}
            <Panel header="Installment Schedule">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                        <thead className="bg-white dark:bg-slate-950">
                            <tr>
                                {['Installment', 'Due Date', 'Amount Due', 'Remaining Balance', 'Status', 'Bill Date', 'Billed Amount'].map((h) => (
                                    <th
                                        key={h}
                                        className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider dark:text-white"
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                            {plan.installments.map((installment) => (
                                <tr key={installment.id} className="bg-slate-50 dark:bg-slate-950/50">
                                    <td className="px-4 py-3 text-sm font-medium dark:text-gray-200">
                                        Installment {installment.number}
                                    </td>
                                    <td className="px-4 py-3 text-sm dark:text-gray-300">
                                        {formatUTCDetailedDate(new Date(installment.dueDate))}
                                    </td>
                                    <td className="px-4 py-3 text-sm dark:text-gray-300">
                                        {formatCurrency(installment.amount)}
                                    </td>
                                    <td className="px-4 py-3 text-sm dark:text-gray-300">
                                        {formatCurrency(installment.remainingBalance)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge status={installment.status} />
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                        {(installment as any).paidDate
                                            ? formatUTCDetailedDate(new Date((installment as any).paidDate))
                                            : '\u2014'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                        {installment.status === 'PAID' || installment.status === 'CREDITED'
                                            ? formatCurrency((installment as any).paidAmount ?? installment.amount)
                                            : (installment as any).paidAmount != null
                                                ? formatCurrency((installment as any).paidAmount)
                                                : '\u2014'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            <IncentiveActivityLogPanel auditHistory={auditHistory} isLoading={auditLoading} />
        </div>
    )
}

export default PaymentPlanDetail
